import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  IndianRupee,
  Loader2,
  LogOut,
  Mail,
  Plus,
  RefreshCw,
  TrendingUp,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { AdminInvestors, tokenStorage } from "@/lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  active: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  disabled: "bg-rose-500/15 text-rose-200 border-rose-500/30",
};

const inr = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function AdminInvestorsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addInvestmentFor, setAddInvestmentFor] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await AdminInvestors.list();
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const doResend = async (inv) => {
    if (!window.confirm(`Resend activation email to ${inv.email}?`)) return;
    try {
      await AdminInvestors.resendActivation(inv.id);
      setNotice({ ok: true, text: `Activation link re-sent to ${inv.email}.` });
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.detail || e.message });
    }
  };

  const doDisable = async (inv) => {
    if (!window.confirm(`Disable investor ${inv.name}? They will no longer be able to log in.`))
      return;
    try {
      await AdminInvestors.remove(inv.id);
      setNotice({ ok: true, text: `${inv.name} disabled.` });
      await load();
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.detail || e.message });
    }
  };

  const totals = items.reduce(
    (acc, i) => {
      acc.invested += i.total_invested || 0;
      acc.value += i.current_value || 0;
      if (i.status === "active") acc.active += 1;
      if (i.status === "pending") acc.pending += 1;
      return acc;
    },
    { invested: 0, value: 0, active: 0, pending: 0 },
  );

  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/95 backdrop-blur border-b border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/leads"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#F0D788]"
            >
              <ArrowLeft size={12} /> Back to Admin
            </Link>
            <div className="w-px h-6 bg-white/10 mx-2" />
            <h1 className="font-display text-base md:text-lg flex items-center gap-2">
              <TrendingUp size={16} className="text-[#F0D788]" /> Investors
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-rose-400/40 text-xs text-white/80"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricCard label="Total investors" value={items.length} testId="inv-total" />
          <MetricCard label="Active" value={totals.active} accent="text-emerald-300" testId="inv-active" />
          <MetricCard label="Pending activation" value={totals.pending} accent="text-amber-300" testId="inv-pending" />
          <MetricCard
            label="Total AUM"
            value={`₹${inr(totals.invested)}`}
            accent="text-[#F0D788]"
            testId="inv-aum"
          />
        </section>

        <div className="flex items-center gap-3 mb-4">
          <button
            data-testid="new-investor-btn"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-gold font-semibold text-sm"
          >
            <UserPlus size={14} /> New Investor
          </button>
          <button
            onClick={load}
            disabled={loading}
            data-testid="refresh-investors"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {notice && (
          <div
            data-testid="investor-notice"
            className={`mb-6 rounded-xl border px-4 py-3 text-xs flex items-center gap-3 ${
              notice.ok
                ? "bg-emerald-400/10 border-emerald-400/35 text-emerald-100"
                : "bg-rose-400/10 border-rose-400/35 text-rose-100"
            }`}
          >
            {notice.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {notice.text}
            <button onClick={() => setNotice(null)} className="ml-auto opacity-70 underline">
              Dismiss
            </button>
          </div>
        )}

        <section className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Investor</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3"># Investments</th>
                  <th className="text-right px-4 py-3">Total Invested</th>
                  <th className="text-right px-4 py-3">Current Value</th>
                  <th className="text-right px-4 py-3">Target</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-white/55">
                      <Loader2 className="inline animate-spin mr-2" size={13} /> Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-white/55">
                      No investors yet. Click <strong>New Investor</strong> to onboard one.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((inv, i) => (
                    <tr
                      key={inv.id}
                      data-testid={`investor-row-${i}`}
                      className="border-t border-white/5"
                    >
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{inv.name}</div>
                        <div className="text-[11px] text-white/50">{inv.email}</div>
                        <div className="text-[11px] text-white/40">{inv.phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                            STATUS_STYLE[inv.status] || ""
                          }`}
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-white/85">
                        {inv.investment_count || 0}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-white/85">
                        ₹{inr(inv.total_invested)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-300">
                        ₹{inr(inv.current_value)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-[#F0D788]">
                        {(inv.target_return_pct ?? 11.55).toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setAddInvestmentFor(inv)}
                          data-testid={`add-investment-${i}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#D4AF37]/35 hover:border-[#D4AF37]/70 text-[11px] text-[#F0D788] mr-1.5"
                        >
                          <Plus size={11} /> Investment
                        </button>
                        {inv.status === "pending" && (
                          <button
                            onClick={() => doResend(inv)}
                            data-testid={`resend-${i}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-blue-400/40 hover:border-blue-400/70 text-[11px] text-blue-200 mr-1.5"
                          >
                            <Mail size={11} /> Resend
                          </button>
                        )}
                        {inv.status !== "disabled" && (
                          <button
                            onClick={() => doDisable(inv)}
                            data-testid={`disable-${i}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-400/40 hover:border-rose-400/70 text-[11px] text-rose-300"
                          >
                            <Trash2 size={11} /> Disable
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {showAdd && (
        <NewInvestorModal
          onClose={() => setShowAdd(false)}
          onCreated={(msg) => {
            setNotice({ ok: true, text: msg });
            setShowAdd(false);
            load();
          }}
        />
      )}
      {addInvestmentFor && (
        <NewInvestmentModal
          investor={addInvestmentFor}
          onClose={() => setAddInvestmentFor(null)}
          onCreated={(msg) => {
            setNotice({ ok: true, text: msg });
            setAddInvestmentFor(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function MetricCard({ label, value, accent = "text-white", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-[#0A1F44] border border-[#D4AF37]/20 p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{label}</div>
      <div className={`mt-1.5 font-display text-2xl ${accent}`}>{value}</div>
    </div>
  );
}

/* ---------------- New Investor Modal ---------------- */
function NewInvestorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    target_return_pct: 11.55,
    sponsor_partner_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.sponsor_partner_id) delete payload.sponsor_partner_id;
      payload.target_return_pct = Number(form.target_return_pct) || 11.55;
      const r = await AdminInvestors.create(payload);
      onCreated(`Investor ${r.investor.name} created — activation email sent.`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not create investor.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Onboard a new investor" testId="new-investor-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name" required>
          <input
            data-testid="ni-name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className={inputCls}
            placeholder="Ravi Patel"
          />
        </Field>
        <Field label="Email address" required>
          <input
            data-testid="ni-email"
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={inputCls}
            placeholder="investor@example.com"
          />
        </Field>
        <Field label="Mobile" required>
          <input
            data-testid="ni-phone"
            required
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className={inputCls}
            placeholder="+91 XXXXX XXXXX"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target return % p.a.">
            <input
              data-testid="ni-target"
              type="number"
              step="0.01"
              min="0"
              max="50"
              value={form.target_return_pct}
              onChange={(e) => setForm((f) => ({ ...f, target_return_pct: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <Field label="Sponsor partner ID (optional)">
            <input
              data-testid="ni-sponsor"
              value={form.sponsor_partner_id}
              onChange={(e) => setForm((f) => ({ ...f, sponsor_partner_id: e.target.value }))}
              className={inputCls}
              placeholder="Leave blank if none"
            />
          </Field>
        </div>

        {error && (
          <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="ni-submit"
            className="px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
          >
            {busy ? "Creating…" : "Create & send activation"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- New Investment Modal ---------------- */
function NewInvestmentModal({ investor, onClose, onCreated }) {
  const [form, setForm] = useState({
    principal_amount: "",
    tenure_months: 36,
    target_return_pct: investor.target_return_pct || 11.55,
    invested_at: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        principal_amount: Number(form.principal_amount),
        tenure_months: Number(form.tenure_months),
        target_return_pct: Number(form.target_return_pct),
        invested_at: new Date(form.invested_at).toISOString(),
        notes: form.notes,
      };
      const r = await AdminInvestors.addInvestment(investor.id, payload);
      onCreated(
        `Investment of ₹${inr(r.investment.principal_amount)} recorded for ${investor.name}.`,
      );
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Could not record investment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title={`Record investment · ${investor.name}`}
      testId="new-investment-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Principal amount (₹)" required>
          <div className="relative">
            <IndianRupee
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              data-testid="inv-principal"
              required
              type="number"
              min="1"
              step="1"
              value={form.principal_amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, principal_amount: e.target.value }))
              }
              className={`${inputCls} pl-8`}
              placeholder="500000"
            />
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tenure (months)" required>
            <input
              data-testid="inv-tenure"
              required
              type="number"
              min="1"
              max="600"
              value={form.tenure_months}
              onChange={(e) => setForm((f) => ({ ...f, tenure_months: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <Field label="Target return % p.a.">
            <input
              data-testid="inv-target"
              type="number"
              step="0.01"
              min="0"
              max="50"
              value={form.target_return_pct}
              onChange={(e) => setForm((f) => ({ ...f, target_return_pct: e.target.value }))}
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Invested on" required>
          <input
            data-testid="inv-date"
            required
            type="date"
            value={form.invested_at}
            onChange={(e) => setForm((f) => ({ ...f, invested_at: e.target.value }))}
            className={inputCls}
          />
        </Field>

        <Field label="Internal notes (optional)">
          <textarea
            data-testid="inv-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            className={`${inputCls} min-h-[60px]`}
            placeholder="Cheque number, bank reference etc."
          />
        </Field>

        {error && (
          <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="inv-submit"
            className="px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
          >
            {busy ? "Recording…" : "Record investment & generate PDF"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- Utility components ---------------- */
const inputCls =
  "w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white placeholder:text-white/30";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
        {label}
        {required && <span className="text-[#D4AF37]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function Modal({ title, testId, children, onClose }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="w-full max-w-lg bg-[#0A1F44] rounded-3xl border border-[#D4AF37]/30 p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
